import { useCallback, useEffect, useRef, useState } from 'react'
import { EditRoomType } from '@/hooks/use-edit-guest-rooms'
import { GuestRoomAssignStatusResponseType } from '@/models/room-manager/response/guest-room-assign-status'
import { listRowRoomNumbers } from '@/utils/room-assign-rows'

type GuestRoomAssignStatusList = GuestRoomAssignStatusResponseType['guestRoomAssignStatusList']

type ScrollRequest = { rooms: EditRoomType[]; reservationRoomTypeName?: string | null }

type ScrollTarget = { guestRoomId: string } | { group: string }

// 部屋の行が無いとき（未割り当て）の合わせ先。従来どおり roomTypeId が最小の部屋タイプにする
const findLeastRoomTypeIdTypeName = (rooms: EditRoomType[]) => {
  const leastRoomTypeId = Math.min(...rooms.map(room => Number(room.roomTypeId)))
  return rooms.find(room => Number(room.roomTypeId) === leastRoomTypeId)?.typeNameJa ?? undefined
}

/**
 * 予約の部屋のうち、表で一番上にある部屋の行を返す（予約のバーをクリックして開いたときは、rooms はクリックした部屋だけ）。
 * 行が無いときは見出しを返す（見出しを閉じているときはその見出し、未割り当てのときは申し込みの部屋タイプの見出し）
 */
const findReservationRoomsScrollTarget = (
  { rooms, reservationRoomTypeName }: ScrollRequest,
  guestRooms: GuestRoomAssignStatusList,
  hiddenRoomNameList: string[],
): ScrollTarget | undefined => {
  const guestRoomIds = rooms.map(room => room.guestRoomId).filter(Boolean)
  let collapsedGroup: string | undefined

  for (const group of Object.keys(guestRooms)) {
    if (!group) continue
    for (const roomNumber of listRowRoomNumbers(guestRooms[group])) {
      const { guestRoomId } = guestRooms[group][roomNumber]
      if (!guestRoomIds.includes(guestRoomId)) continue
      if (!hiddenRoomNameList.includes(group)) return { guestRoomId }
      collapsedGroup = collapsedGroup ?? group
    }
  }
  if (collapsedGroup) return { group: collapsedGroup }

  const roomTypeName = reservationRoomTypeName || findLeastRoomTypeIdTypeName(rooms)
  return roomTypeName ? { group: roomTypeName } : undefined
}

const registerElement = (elements: Map<string, HTMLElement>, key: string) => (element: HTMLElement | null) => {
  if (element) elements.set(key, element)
  else elements.delete(key)
}

/**
 * アサイン画面の表を縦にスクロールして、予約の部屋の行（予約のバーをクリックしたときはその部屋の行。行が無ければ見出し）を固定表示の見出しのすぐ下に出す。横は左端に戻す。
 * 予約 ID 付きで開いたときは予約より後に表のデータが届くことがあるため、スクロールは表のデータが届いて描画されてから行う
 */
export const useCalendarScrollForAssign = ({
  guestRooms,
  hiddenRoomNameList,
}: {
  guestRooms: GuestRoomAssignStatusList | undefined
  hiddenRoomNameList: string[]
}) => {
  const [scrollRequest, setScrollRequest] = useState<ScrollRequest>()
  // 予約を開く処理は URL の予約 ID の変化から始まり、URL には部屋が載らない。バーのクリックから予約を読み込むまで、クリックした部屋をここで覚える
  const clickedRoom = useRef<{ reservationId: string; guestRoomId?: string }>()
  const roomRowElements = useRef(new Map<string, HTMLElement>())
  const roomGroupElements = useRef(new Map<string, HTMLElement>())

  const rememberClickedRoom = useCallback((reservationId: string, guestRoomId?: string) => {
    clickedRoom.current = { reservationId, guestRoomId }
  }, [])
  // クリックした部屋は、そのクリックで開いた1回だけ使う。保存のあとの読み直しや、ほかの入口から開いたときは部屋の選択が無い
  const scrollToReservationRooms = useCallback(
    (
      rooms: EditRoomType[],
      { reservationId, reservationRoomTypeName }: { reservationId?: string; reservationRoomTypeName?: string | null } = {},
    ) => {
      const clicked = reservationId && clickedRoom.current?.reservationId === reservationId ? clickedRoom.current : undefined
      if (clicked) clickedRoom.current = undefined
      const clickedRooms = rooms.filter(room => room.guestRoomId && room.guestRoomId === clicked?.guestRoomId)
      setScrollRequest({ rooms: clickedRooms.length ? clickedRooms : rooms, reservationRoomTypeName })
    },
    [],
  )
  const registerRoomRow = useCallback((guestRoomId: string) => registerElement(roomRowElements.current, guestRoomId), [])
  const registerRoomGroup = useCallback((group: string) => registerElement(roomGroupElements.current, group), [])

  useEffect(() => {
    if (!scrollRequest || !guestRooms) return
    setScrollRequest(undefined)

    const target = findReservationRoomsScrollTarget(scrollRequest, guestRooms, hiddenRoomNameList)
    const targetElement =
      target && ('guestRoomId' in target ? roomRowElements.current.get(target.guestRoomId) : roomGroupElements.current.get(target.group))
    const container = document.getElementById('roomsContainer')
    // 日付の行の下に残室数の行も固定表示される。日付の行の下端に合わせると、対象の上29pxが残室数の行に隠れる
    const remainingRoomTotal = document.getElementById('remainingRoomTotal')
    if (!targetElement || !container || !remainingRoomTotal) return

    container.scrollTo(
      0,
      container.scrollTop + targetElement.getBoundingClientRect().top - remainingRoomTotal.getBoundingClientRect().bottom,
    )
  }, [scrollRequest, guestRooms, hiddenRoomNameList])

  return { rememberClickedRoom, scrollToReservationRooms, registerRoomRow, registerRoomGroup }
}
